import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { requestPasswordRecovery } from '../../redux/authSlice';
import { Form, Button, Alert, Container, Row, Col, Card } from 'react-bootstrap';

const RecoverPasswordComponent = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isBlocked, setIsBlocked] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const dispatch = useDispatch();
  const { recoveryMessage, error } = useSelector((state) => state.auth);

  // Configuración de seguridad (igual que en login)
  const MAX_ATTEMPTS = 3;
  const BLOCK_TIME = 30; // 30 segundos de bloqueo

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isBlocked) {
      return;
    }

    const newErrors = {};
    if (!email) {
      newErrors.email = 'El correo electrónico es obligatorio.';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Por favor, introduce un correo válido.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      dispatch(requestPasswordRecovery(email.trim()));
      setAttempts(prev => prev + 1);
    }
  };

  // Manejo de errores y bloqueos (igual que en login)
  useEffect(() => {
    if (error) {
      // Bloquear después de 3 intentos fallidos
      if (attempts >= MAX_ATTEMPTS) {
        setIsBlocked(true);
        setCooldown(BLOCK_TIME);
      }

      // Manejar error 429 (Too Many Requests)
      if (error.response?.status === 429) {
        setIsBlocked(true);
        setCooldown(error.response?.data?.retryAfter || BLOCK_TIME);
      }
    }
  }, [error, attempts]);

  // Temporizador de cooldown (igual que en login)
  useEffect(() => {
    let timer;
    if (isBlocked && cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => prev - 1);
      }, 1000);
    } else if (cooldown === 0 && isBlocked) {
      setIsBlocked(false);
    }
    return () => clearInterval(timer);
  }, [isBlocked, cooldown]);

  // Función para obtener mensaje de error seguro (similar a login)
  const getSafeErrorMessage = () => {
    if (!error) return null;

    switch (error.response?.status) {
      case 400:
        return 'Solicitud incorrecta. Verifique los datos ingresados.';
      case 404:
        return 'Si el correo existe, recibirá un enlace de recuperación.';
      case 429:
        return `Demasiadas solicitudes. Por favor espere ${cooldown} segundos.`;
      case 500:
        return 'Error en el servidor. Por favor intente más tarde.';
      default:
        return 'Error al procesar su solicitud. Por favor intente nuevamente.';
    }
  };

  return (
    <Container
      className="d-flex justify-content-center align-items-center min-vh-100 bg-light"
      role="main"
      aria-label="Página de recuperación de contraseña"
    >
      <Row className="w-100">
        <Col md={6} lg={4} className="mx-auto">
          <Card className="shadow" aria-labelledby="recovery-card-title">
            <Card.Body>
              <h3
                className="text-center text-primary mb-4"
                id="recovery-card-title"
                aria-level="1"
              >
                Recuperar Contraseña
              </h3>
              <p className="text-center text-muted" aria-live="polite">
                Ingrese su correo electrónico para recuperar su cuenta.
              </p>
              <Form onSubmit={handleSubmit} noValidate aria-label="Formulario de recuperación de contraseña">
                <Form.Group controlId="formEmail">
                  <Form.Label htmlFor="recoveryEmailInput">Correo Electrónico</Form.Label>
                  <Form.Control
                    id="recoveryEmailInput"
                    type="email"
                    placeholder="Introduce tu correo electrónico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isInvalid={!!errors.email}
                    disabled={isBlocked}
                    aria-label="Correo electrónico para recuperación"
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "emailErrorFeedback" : undefined}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    id="emailErrorFeedback"
                    role="alert"
                  >
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mt-3"
                  disabled={isBlocked || !!recoveryMessage}
                  aria-label={isBlocked ?
                    `Espera ${cooldown} segundos antes de intentar nuevamente` :
                    'Enviar solicitud de recuperación de contraseña'
                  }
                  aria-busy={isBlocked}
                >
                  {isBlocked ? `Espera ${cooldown}s...` : 'Recuperar Contraseña'}
                </Button>
              </Form>

              {/* Mensaje de éxito */}
              {recoveryMessage && (
                <Alert
                  variant="success"
                  className="mt-4 text-center"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  Si su email está registrado, se enviará un correo. Verifique su casilla de correos recibidos.
                </Alert>
              )}

              {/* Mensaje de error */}
              {error && (
                <Alert
                  variant="danger"
                  className="mt-4 text-center"
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                >
                  {getSafeErrorMessage()}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RecoverPasswordComponent;